import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { alpha, Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { CloseCircle, DocumentDownload, Eye } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { PdfIcon } from '@ui/assets/custom-icons/pdf-icon';
const LanguagePdfUploadField = ({ id = 'language-pdf-upload', languages, filesByLanguage = {}, onLanguageFilesChange, languageErrors = {}, disabled = false, maxFileSizeMb = 20, existingFilesByLanguage = {} }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const fileInputRefs = useRef({});
    const [dragOverLanguage, setDragOverLanguage] = useState(null);
    const [fileValidationErrors, setFileValidationErrors] = useState({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewIsObjectUrl, setPreviewIsObjectUrl] = useState(false);
    const [previewLanguageId, setPreviewLanguageId] = useState('');
    const [removedExisting, setRemovedExisting] = useState(new Set());
    useEffect(() => {
        setRemovedExisting(new Set());
    }, [existingFilesByLanguage]);
    const notifyChange = (next) => {
        onLanguageFilesChange?.(next);
    };
    const handleFileSelect = (file, languageId) => {
        const maxSize = maxFileSizeMb * 1024 * 1024;
        const errors = { ...fileValidationErrors };
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            errors[languageId] =
                t('fileUpload.fileTypeError', { field: 'PDF' }) || 'Only PDF files are allowed.';
            setFileValidationErrors(errors);
            return;
        }
        if (file.size > maxSize) {
            errors[languageId] =
                t('validations.fileSizeTooLarge', { fileNumber: 1, maxSize: `${maxFileSizeMb}MB` }) ||
                    `File size must be less than ${maxFileSizeMb}MB.`;
            setFileValidationErrors(errors);
            return;
        }
        delete errors[languageId];
        setFileValidationErrors(errors);
        const next = { ...filesByLanguage, [languageId]: [file] };
        notifyChange(next);
    };
    const handleFileInputChange = (event, languageId) => {
        const file = event.target.files?.[0] ?? null;
        if (file)
            handleFileSelect(file, languageId);
        event.target.value = '';
    };
    const handleDrop = (event, languageId) => {
        event.preventDefault();
        setDragOverLanguage(null);
        if (disabled)
            return;
        const file = event.dataTransfer.files?.[0] ?? null;
        if (file)
            handleFileSelect(file, languageId);
        event.dataTransfer.clearData();
    };
    const handleDragOver = (event, languageId) => {
        event.preventDefault();
        if (!disabled)
            setDragOverLanguage(languageId);
    };
    const handleDragLeave = () => {
        setDragOverLanguage(null);
    };
    const handleUploadClick = (languageId) => {
        if (!disabled) {
            fileInputRefs.current[languageId]?.click();
        }
    };
    const handleRemoveFile = (languageId) => {
        if (disabled)
            return;
        const nextArr = [...(filesByLanguage[languageId] || [])];
        nextArr.splice(0, 1);
        const next = { ...filesByLanguage, [languageId]: nextArr };
        notifyChange(next);
        setFileValidationErrors((prev) => {
            const updated = { ...prev };
            delete updated[languageId];
            return updated;
        });
        if (fileInputRefs.current[languageId]) {
            fileInputRefs.current[languageId].value = '';
        }
    };
    const handlePreview = (languageId) => {
        const file = filesByLanguage[languageId]?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setPreviewIsObjectUrl(true);
        }
        else {
            const existing = existingFilesByLanguage[languageId];
            if (!existing)
                return;
            setPreviewUrl(existing.url);
            setPreviewIsObjectUrl(false);
        }
        setPreviewLanguageId(languageId);
        setPreviewOpen(true);
    };
    const handleDownload = (languageId) => {
        const file = filesByLanguage[languageId]?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
        else {
            const existing = existingFilesByLanguage[languageId];
            if (!existing)
                return;
            window.open(existing.url, '_blank', 'noopener,noreferrer');
        }
    };
    const handleClosePreview = () => {
        setPreviewOpen(false);
        if (previewUrl && previewIsObjectUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setPreviewLanguageId('');
    };
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: [languages.map((language) => {
                const langId = language.id;
                const langFile = filesByLanguage[langId]?.[0];
                const existingFile = !langFile && !removedExisting.has(langId) ? existingFilesByLanguage[langId] : undefined;
                const hasFile = Boolean(langFile) || Boolean(existingFile);
                const displayName = langFile?.name ?? existingFile?.name ?? '';
                const isDragOver = dragOverLanguage === langId;
                const langError = languageErrors[langId] || fileValidationErrors[langId];
                return (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'stretch',
                        padding: 1,
                        border: langError
                            ? `1px solid ${theme.palette.error.main}`
                            : `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                        borderRadius: '14px',
                        backgroundColor: 'transparent',
                        gap: 1,
                        flexDirection: isSmallScreen ? 'column' : 'row'
                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 60 }, children: [_jsx(Box, { component: 'img', src: language.flagUrl || '/assets/images/flags/empty-flag.svg', alt: language.name, sx: {
                                        width: 24,
                                        height: 18,
                                        objectFit: 'cover',
                                        borderRadius: 0.5
                                    } }), _jsx(Typography, { variant: 'body2', fontWeight: 500, children: (language.code || language.id).toUpperCase() })] }), _jsx(Divider, { orientation: 'vertical', flexItem: true, sx: {
                                borderColor: theme.palette.grey[400],
                                borderWidth: '1px',
                                alignSelf: 'stretch',
                                display: isSmallScreen ? 'none' : 'block'
                            } }), _jsxs(Box, { sx: { flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }, children: [!hasFile ? (_jsx(Box, { sx: {
                                        width: '100%',
                                        borderRadius: 1.5,
                                        overflow: 'hidden',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        backgroundColor: isDragOver
                                            ? alpha(theme.palette.primary.main, 0.05)
                                            : langError
                                                ? alpha(theme.palette.error.main, 0.08)
                                                : theme.palette.common.grey8,
                                        border: isDragOver
                                            ? `1px dashed ${theme.palette.primary.main}`
                                            : langError
                                                ? `1px dashed ${theme.palette.error.main}`
                                                : `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
                                        transition: 'all 0.2s ease-in-out'
                                    }, onDrop: (e) => handleDrop(e, langId), onDragOver: (e) => handleDragOver(e, langId), onDragLeave: handleDragLeave, onClick: () => handleUploadClick(langId), children: _jsx(Box, { sx: { padding: 2.5, textAlign: 'center' }, children: _jsx(Typography, { variant: 'body2', sx: {
                                                color: disabled ? theme.palette.grey[400] : theme.palette.text.disabled,
                                                fontWeight: 400
                                            }, children: t('fileUpload.uploadFile') }) }) })) : (_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: 1,
                                        borderRadius: 1,
                                        border: isDragOver
                                            ? `2px dashed ${theme.palette.primary.main}`
                                            : `1px solid ${theme.palette.grey[300]}`,
                                        backgroundColor: isDragOver
                                            ? alpha(theme.palette.primary.main, 0.05)
                                            : theme.palette.common.white,
                                        transition: 'all 0.2s ease-in-out',
                                        minWidth: 0,
                                        gap: 1
                                    }, onDrop: (e) => handleDrop(e, langId), onDragOver: (e) => handleDragOver(e, langId), onDragLeave: handleDragLeave, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }, children: [_jsx(Box, { sx: { flexShrink: 0, display: 'flex' }, children: _jsx(PdfIcon, { width: '22px', height: '22px' }) }), _jsx(Typography, { variant: 'body2', fontWeight: 400, color: theme.palette.text.primary, title: displayName, sx: {
                                                        flex: 1,
                                                        minWidth: 0,
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }, children: displayName })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }, children: [_jsx(Tooltip, { title: t('common.pdfPreview'), children: _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                            e.stopPropagation();
                                                            handlePreview(langId);
                                                        }, sx: { border: 'none', padding: 0.5 }, children: _jsx(Eye, { variant: 'Outline', width: 18, height: 18 }) }) }), _jsx(Tooltip, { title: t('common.download'), children: _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                            e.stopPropagation();
                                                            handleDownload(langId);
                                                        }, sx: { border: 'none', padding: 0.5 }, children: _jsx(DocumentDownload, { variant: 'Outline', width: 18, height: 18 }) }) }), _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                        e.stopPropagation();
                                                        if (langFile) {
                                                            handleRemoveFile(langId);
                                                        }
                                                        else {
                                                            setRemovedExisting((prev) => new Set(prev).add(langId));
                                                        }
                                                    }, disabled: disabled, sx: { border: 'none', padding: 0.5 }, children: _jsx(Icon, { icon: 'eva:close-fill', width: '18', height: '18' }) })] })] })), langError && (_jsx(Typography, { variant: 'caption', color: 'error', sx: { display: 'block', ml: 1 }, children: langError }))] }), _jsx("input", { ref: (el) => {
                                fileInputRefs.current[langId] = el;
                            }, id: `${id}-input-${langId}`, type: 'file', accept: '.pdf,application/pdf', onChange: (e) => handleFileInputChange(e, langId), style: { display: 'none' }, disabled: disabled })] }, langId));
            }), _jsxs(Dialog, { open: previewOpen, onClose: handleClosePreview, maxWidth: 'lg', fullWidth: true, PaperProps: {
                    sx: {
                        height: '90vh',
                        maxHeight: '90vh'
                    }
                }, children: [_jsx(DialogTitle, { children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }, children: [_jsxs(Typography, { variant: 'h6', children: [t('common.pdfPreview'), " -", ' ', languages.find((l) => l.id === previewLanguageId)?.code?.toUpperCase() ||
                                            languages.find((l) => l.id === previewLanguageId)?.id.toUpperCase()] }), _jsx(IconButton, { onClick: handleClosePreview, size: 'small', children: _jsx(CloseCircle, { size: 20 }) })] }) }), _jsx(DialogContent, { sx: { padding: 0, height: '100%' }, children: previewUrl && (_jsx(Box, { component: 'iframe', src: previewUrl, sx: {
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                minHeight: '70vh'
                            } })) })] })] }));
};
export default LanguagePdfUploadField;
